import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, Skeleton } from "@mui/material";
import {
  DesignSystem,
  ContentBlockProperties,
} from "../../../types/ThemeTypes";
import { defaultContentBlockProps } from "../../../config/defaultLandingConfig";
import { isVideoFile } from "../../../utils/isVideoFile";
import { ReplayIcon, MuteIcon, UnMuteIcon } from "@/components/icons";
import styles from "./ContentBlock.module.css";
import { useViewport } from "../../../hooks/useViewPort";
import SandboxedHtmlFrame from "../../share/SandboxedHtmlFrame";
import {
  resolveContentMode,
  resolveEmbedHtml,
} from "../../../utils/contentMode";

export interface ContentBlockProps {
  isPublished?: boolean;
  properties?: ContentBlockProperties;
  designSystem?: DesignSystem;
  previewMode?: string;
}

/**
 * Helper function to format border CSS value
 */
const formatBorder = (width: string, color: string): string => {
  if (!width || width.trim() === "" || width === "0px" || width === "0") {
    return "none";
  }
  return `${width} solid ${color || "#000000"}`;
};

const ContentBlock: React.FC<ContentBlockProps> = ({
  isPublished = false,
  properties,
  designSystem,
  previewMode,
}) => {
  const { isMobileView, isTabletView } = useViewport();
  const videoRef = useRef<HTMLVideoElement>(null);

  const props = useMemo(
    () => ({
      ...defaultContentBlockProps,
      ...properties,
    }),
    [properties]
  );

  // Hero media: mobile + tablet share compact asset; desktop uses desktop asset only.
  const mediaSource =
    isTabletView || isMobileView
      ? props.heroImageMobile || props.heroImage
      : props.heroImage;

  const isVideo = mediaSource ? isVideoFile(mediaSource) : false;

  // One alt for both media; empty means decorative
  const heroAlt = props.heroAlt?.trim() ?? "";

  // Media or Embed HTML, per the CMS toggle. Mobile + tablet fall back to the
  // large-screen embed.
  const isEmbedMode = resolveContentMode(props) === "embedHtml";
  const embedHtml = resolveEmbedHtml(props, isTabletView || isMobileView);

  const [isLoading, setIsLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);

  // Reset loading when source changes
  useEffect(() => {
    if (mediaSource) {
      setIsLoading(true);
    }
  }, [mediaSource]);

  // Cached images may not fire onLoad on mobile reload — check img.complete after render
  useEffect(() => {
    if (!isVideo && isLoading && imgRef.current?.complete) {
      setIsLoading(false);
    }
  }, [isVideo, isLoading]);

  const handleLoad = () => setIsLoading(false);
  const handleError = () => setIsLoading(false);

  const handleReload = () => {
    if (isVideo && videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  };

  const handleToggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    if (isVideo && videoRef.current) {
      videoRef.current.muted = newMutedState;
    }
  };

  // Sync muted state when video loads (single ref, no dual-audio possible)
  useEffect(() => {
    if (isVideo && videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted, isVideo]);

  const borderStyle = {
    borderTop: formatBorder(props.borderTop, props.borderColor),
    borderRight: formatBorder(props.borderRight, props.borderColor),
    borderBottom: formatBorder(props.borderBottom, props.borderColor),
    borderLeft: formatBorder(props.borderLeft, props.borderColor),
  };

  if (isEmbedMode) {
    return (
      <Box
        className={styles.bb_minimalism_embedContainer}
        style={{ backgroundColor: props.heroBackgroundColor, ...borderStyle }}
      >
        {embedHtml && <SandboxedHtmlFrame html={embedHtml} />}
      </Box>
    );
  }

  return (
    <Box
      className={styles.bb_minimalism_contentContainer}
      style={{
        backgroundColor: props.heroBackgroundColor,
        ...borderStyle,
        ...(isMobileView && {
          height: "100%",
        }),
      }}
    >
      {mediaSource && (
        <Box className={styles.bb_minimalism_imageWrapper}>
          {isLoading && (
            <Box className={styles.bb_minimalism_skeletonWrapper}>
              <Skeleton
                className={styles.bb_minimalism_skeleton}
                variant="rectangular"
                animation="wave"
              />
            </Box>
          )}

          {isVideo ? (
            <video
              ref={videoRef}
              className={styles.bb_minimalism_heroMedia}
              src={mediaSource}
              aria-label={heroAlt || undefined}
              aria-hidden={heroAlt ? undefined : true}
              autoPlay
              loop
              muted={isMuted}
              playsInline
              onLoadedData={handleLoad}
              onError={handleError}
              style={{ opacity: isLoading ? 0 : 1 }}
            />
          ) : (
            <img
              ref={imgRef}
              className={styles.bb_minimalism_heroMedia}
              src={mediaSource}
              alt={heroAlt}
              onLoad={handleLoad}
              onError={handleError}
              style={{ opacity: isLoading ? 0 : 1 }}
            />
          )}
        </Box>
      )}

      {isVideo && (
        <Box
          sx={{
            position: "absolute",
            bottom: "16px",
            left: "16px",
            display: "flex",
            flexDirection: "column",
            zIndex: 10,
          }}
        >
          <Box
            onClick={handleReload}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              transition: "background-color 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.7)",
              },
            }}
          >
            <ReplayIcon size={24} color="#fff" />
          </Box>

          <Box
            onClick={handleToggleMute}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              transition: "background-color 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.7)",
              },
            }}
          >
            {isMuted ? (
              <MuteIcon size={24} color="#fff" />
            ) : (
              <UnMuteIcon size={24} color="#fff" />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ContentBlock;
